import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
    Box,
    ButtonBase,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import WorkIcon from '@material-ui/icons/Work'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import DescriptionIcon from '@material-ui/icons/Description'
import SecurityIcon from '@material-ui/icons/Security'
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd'
import { Documents } from './Documents'
import { Contracts } from './Contracts'
import { Insurance } from './Insurance'
import { W9Manager } from './W9Manager'

type TabType = 'contracts' | 'certificates' | 'documents' | 'insurance' | 'w9'

const TAB_VALUES: TabType[] = [
    'contracts',
    'certificates',
    'documents',
    'insurance',
    'w9',
]

function tabFromSearch(search: string): TabType {
    const tab = new URLSearchParams(search).get('tab')
    if (tab && TAB_VALUES.includes(tab as TabType)) {
        return tab as TabType
    }
    return 'contracts'
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 104px)',
        width: '100%',
        backgroundColor: theme.palette.background.default,
    },
    header: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2, 3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    logo: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        fontSize: '16px',
    },
    headerTitle: {
        fontSize: '20px',
        fontWeight: 700,
        letterSpacing: '-0.02em',
    },
    avatar: {
        height: 32,
        width: 32,
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[200],
        border: `1px solid ${theme.palette.divider}`,
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    tabsInner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(4),
        padding: theme.spacing(0, 3),
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 0),
        fontSize: '14px',
        fontWeight: 500,
        borderBottom: '2px solid transparent',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.text.primary,
            borderBottomColor: theme.palette.divider,
        },
    },
    activeTab: {
        color: theme.palette.primary.main,
        borderBottomColor: theme.palette.primary.main,
        '&:hover': {
            color: theme.palette.primary.main,
            borderBottomColor: theme.palette.primary.main,
        },
    },
    tabIcon: {
        fontSize: '18px',
    },
    mainContent: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(1),
    },
}))

export const ContractManager = () => {
    const classes = useStyles()
    const location = useLocation()
    const history = useHistory()
    const [activeTab, setActiveTab] = useState<TabType>(() =>
        tabFromSearch(location.search),
    )

    useEffect(() => {
        setActiveTab(tabFromSearch(location.search))
    }, [location.search])

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab)
        const params = new URLSearchParams(location.search)
        if (tab === 'contracts') {
            params.delete('tab')
        } else {
            params.set('tab', tab)
        }
        const qs = params.toString()
        history.replace({
            pathname: location.pathname,
            search: qs ? `?${qs}` : '',
        })
    }

    return (
        <Box className={classes.root}>
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.logo}>C</Box>
                    <Typography className={classes.headerTitle}>
                        Contract Manager
                    </Typography>
                </Box>
            </Box>

            {/* Tab Navigation */}
            <Box className={classes.tabsContainer}>
                <Box className={classes.tabsInner}>
                    <ButtonBase
                        onClick={() => handleTabChange('contracts')}
                        className={`${classes.tab} ${
                            activeTab === 'contracts' ? classes.activeTab : ''
                        }`}
                    >
                        <WorkIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Contracts
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('certificates')}
                        className={`${classes.tab} ${
                            activeTab === 'certificates'
                                ? classes.activeTab
                                : ''
                        }`}
                    >
                        <VerifiedUserIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Certificates
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('documents')}
                        className={`${classes.tab} ${
                            activeTab === 'documents' ? classes.activeTab : ''
                        }`}
                    >
                        <DescriptionIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Documents
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('insurance')}
                        className={`${classes.tab} ${
                            activeTab === 'insurance' ? classes.activeTab : ''
                        }`}
                    >
                        <SecurityIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            Insurance
                        </Typography>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => handleTabChange('w9')}
                        className={`${classes.tab} ${
                            activeTab === 'w9' ? classes.activeTab : ''
                        }`}
                    >
                        <AssignmentIndIcon className={classes.tabIcon} />
                        <Typography variant="body2" component="span">
                            W-9s
                        </Typography>
                    </ButtonBase>
                </Box>
            </Box>

            {/* Main Content Area */}
            <Box className={classes.mainContent}>
                {activeTab === 'contracts' && <Contracts />}
                {activeTab === 'certificates' && (
                    <Contracts variant="certificates" />
                )}
                {activeTab === 'documents' && <Documents />}
                {activeTab === 'insurance' && <Insurance />}
                {activeTab === 'w9' && <W9Manager />}
            </Box>
        </Box>
    )
}
